import { dayStartMs, dayStartShifted, formatDayLabel } from "./shared";
import type {
  AverageRateItem,
  DashboardResponse,
  RateOfOperationData,
  TimeChartEvent,
} from "./dashboardResponse";

/**
 * Deterministic mock backend for the Running Status page (no CubeJS yet):
 * one `DashboardResponse` with 30 calendar days of generated events
 * (ending on the anchor day) and the share/average sections aggregated
 * from them.
 *
 * Each widget's `state.ts` fetcher reads its own section of the same
 * response, so swapping in the real API is a body-only change.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** The newest mock day — the 30 days count back from here. */
const MOCK_LAST_DAY = "22/09/2026";

/** Days in the month scope. */
export const MOCK_MONTH_DAYS = 30;

/** Mock network latency (the spec example's 500ms). */
const MOCK_LATENCY_MS = 500;

/** Percent share, rounded to two decimals. */
function pct(durationMs: number): number {
  return Math.round((durationMs / DAY_MS) * 10000) / 100;
}

/** Seeded PRNG (LCG) so the fake data is stable across reloads. */
function makeRand(seed = 20260922) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/** Statuses weighted like a real production day: printing dominates. */
const WEIGHTED_STATUSES: TimeChartEvent["status"][] = [
  "1.Print",
  "1.Print",
  "1.Print",
  "1.Print",
  "2.Adjustment",
  "3.Cleaning",
  "4.Error",
  "5.ON",
];

/** Random duration between 10 min and `maxMinutes`. */
function randomDuration(rand: () => number, maxMinutes: number): number {
  return Math.round((10 + rand() * (maxMinutes - 10)) * MINUTE_MS);
}

/**
 * Generates the month's `time_chart_events`: one day at a time (oldest →
 * newest), each day a chronological sequence of interrupted segments —
 * print runs, adjustments, cleanings, errors, ON idle and OFF gaps.
 */
function generateMonthEvents(): TimeChartEvent[] {
  const rand = makeRand();
  const events: TimeChartEvent[] = [];

  for (let i = 0; i < MOCK_MONTH_DAYS; i += 1) {
    // Calendar-exact shift back from the anchor (no DST drift).
    const dayStart = dayStartShifted(MOCK_LAST_DAY, -(MOCK_MONTH_DAYS - 1 - i));
    const date = formatDayLabel(dayStart);

    // The machine's shift: starts between 6:30 and 7:30, ends by ~22:30.
    let cursor = dayStart + (6 * 60 + 30 + Math.floor(rand() * 60)) * MINUTE_MS;
    const endOfDay = dayStart + 22 * HOUR_MS + 30 * MINUTE_MS;

    while (cursor < endOfDay) {
      // Occasionally a longer OFF block (break / end of shift).
      const isOff = rand() < 0.12;
      const status = isOff ? "6.OFF" : WEIGHTED_STATUSES[Math.floor(rand() * WEIGHTED_STATUSES.length)];
      const duration =
        status === "6.OFF"
          ? randomDuration(rand, 90)
          : status === "4.Error"
            ? randomDuration(rand, 45)
            : randomDuration(rand, 150);
      const end = Math.min(cursor + duration, endOfDay);

      events.push({ date, status, start_time: cursor, end_time: end });

      // Interrupted timeline: a gap before the next segment.
      cursor = end + (rand() < 0.35 ? randomDuration(rand, 40) : MINUTE_MS);
    }
  }

  return events;
}

/**
 * Aggregates the month's events into `rate_of_operation`: per-day share
 * of each operated status (duration / 24h), everything outside
 * Print / Adjustment / Cleaning counting as Not Active. Each day's four
 * shares sum to exactly 100. Categories are newest-first.
 */
function summarizeRateOfOperation(events: TimeChartEvent[]): RateOfOperationData {
  const days: string[] = [];
  const durations = new Map<string, { print: number; adjustment: number; cleaning: number }>();
  for (const event of events) {
    if (!durations.has(event.date)) {
      days.push(event.date);
      durations.set(event.date, { print: 0, adjustment: 0, cleaning: 0 });
    }
    const bucket = durations.get(event.date)!;
    const duration = Math.max(0, event.end_time - event.start_time);
    if (event.status === "1.Print") bucket.print += duration;
    else if (event.status === "2.Adjustment") bucket.adjustment += duration;
    else if (event.status === "3.Cleaning") bucket.cleaning += duration;
  }

  // Oldest → newest, then flip to newest-first.
  days.sort((a, b) => dayStartMs(a) - dayStartMs(b));
  const ordered = [...days].reverse();

  const series = ordered.map((day) => {
    const bucket = durations.get(day)!;
    const print = pct(bucket.print);
    const adjustment = pct(bucket.adjustment);
    const cleaning = pct(bucket.cleaning);
    // Force the row to sum to exactly 100 (remainder = Not Active).
    const notActive = Math.round((100 - print - adjustment - cleaning) * 100) / 100;
    return { print, adjustment, cleaning, not_active: notActive };
  });

  return {
    categories: ordered,
    series_data: {
      print: series.map((s) => s.print),
      adjustment: series.map((s) => s.adjustment),
      cleaning: series.map((s) => s.cleaning),
      not_active: series.map((s) => s.not_active),
    },
  };
}

/** Averages each status's daily share across all mock days (2 decimals). */
function averageRate(rate: RateOfOperationData): AverageRateItem[] {
  const dayCount = rate.categories.length || 1;
  const average = (values: number[]) =>
    Math.round((values.reduce((sum, v) => sum + v, 0) / dayCount) * 100) / 100;
  return [
    { status_name: "1.Print", percentage: average(rate.series_data.print) },
    { status_name: "2.Adjustment", percentage: average(rate.series_data.adjustment) },
    { status_name: "3.Cleaning", percentage: average(rate.series_data.cleaning) },
    { status_name: "4.Not Active", percentage: average(rate.series_data.not_active) },
  ];
}

/** The page response: generated events + sections aggregated from them. */
function monthResponse(): DashboardResponse {
  const events = generateMonthEvents();
  const rate = summarizeRateOfOperation(events);
  return {
    status: 200,
    message: "Success",
    data: {
      rate_of_operation: rate,
      average_rate: averageRate(rate),
      time_chart_events: events,
    },
  };
}

/**
 * Mock dashboard fetch — simulates network latency and returns the full
 * page response (the 30-day mock scope).
 */
export async function fetchDashboardMockData(): Promise<DashboardResponse> {
  await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
  return monthResponse();
}
